import axios from 'axios';

let chimeraProvider = null;

export const TOKEN_KEY = 'tokenmob';
export const LOGIN_PATH = '/login';

/**
 * Pages anyone can open without being signed in. The app still asks the API for
 * /me, /inbox and /myusers on these pages, and without a token the API answers
 * 403 { auth: false } ("No token provided"), which must not bounce an invited
 * user off the account-setup page (or a password reset link) to /login.
 */
export const PUBLIC_PATHS = ['/login', '/newuser', '/forgotpassword', '/resetpassword', '/auth/microsoft/callback'];
export const isPublicPath = (path = '') =>
  path === '/' || PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`));

/** Whether the failed request was sent with a token (only then can it be "stale"). */
const sentToken = (error) => {
  const headers = error?.config?.headers;
  const auth = headers?.Authorization ?? headers?.authorization ?? (typeof headers?.get === 'function' ? headers.get('Authorization') : undefined);
  return !!auth;
};

export const setChimeraProvider = (provider) => {
  chimeraProvider = provider;
};

export const getAuthToken = () => {
  if (chimeraProvider?.isConnected && chimeraProvider?.auth?.token) {
    return chimeraProvider.auth.token;
  }
  
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * True when a failed request means "your token is missing, invalid or expired".
 *
 * The API's checkAuth answers 403 with `{ auth: false, message }` for every token
 * problem (it never uses 401), so a 403 alone is not enough: other 403s mean
 * "logged in but not allowed" (no_organization, FORBIDDEN, NOT_APP_ADMIN) and
 * must not log the user out. 401 is treated as an auth failure too.
 */
export const isAuthFailure = (error) => {
  const status = error?.response?.status;
  if (status === 401) return true;
  if (status !== 403) return false;
  const body = error?.response?.data;
  return !!body && typeof body === 'object' && body.auth === false;
};

/**
 * Response-error handling shared by every request: when a request sent with a
 * token fails authentication in standalone mode, drop the stale token and go to
 * the login page — unless the user is on a public page (login, account setup,
 * password reset), where they stay put. Requests sent without a token are left
 * alone: signed-in-only pages already redirect through ProtectedRoute.
 * Everything else is passed through.
 */
export const handleResponseError = (
  error,
  {
    connected = !!chimeraProvider?.isConnected,
    clearToken = () => localStorage.removeItem(TOKEN_KEY),
    currentPath = () => window.location.pathname,
    redirect = (path) => {
      window.location.href = path;
    },
  } = {}
) => {
  if (isAuthFailure(error) && !connected && sentToken(error)) {
    clearToken();
    if (!isPublicPath(currentPath())) redirect(LOGIN_PATH);
  }
  return Promise.reject(error);
};

export const setupAxiosInterceptor = () => {
  axios.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use((response) => response, (error) => handleResponseError(error));
};

export const isChimeraMode = () => {
  return chimeraProvider?.isConnected || false;
};
